import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Test } from '@bugbug/core/types/tests';
import { useAsyncLegacyAction } from '~/modules/actionState/hooks/useAsyncLegacyAction';
import { selectSingleProjectId } from '~/modules/project/project.selectors';
import { useAppDispatch, useAppSelector } from '~/modules/store';
import { TestRunActions } from '~/modules/testRun/testRun.redux';
import toasts from '~/services/toasts';

import { testsApi } from '../../tests.api';
import { selectTests } from '../../tests.selectors';

export const useStopTests = () => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectSingleProjectId);
  const testsList = useAppSelector(selectTests);
  const { t } = useTranslation(undefined, { keyPrefix: 'tests.actions.stop' });
  const [stopTestRuns] = useAsyncLegacyAction(TestRunActions.stopRequest);

  const handleStop = useCallback(
    async (ids?: Test['id'][]) => {
      const testRunsIds = ids
        ?.map((id) => testsList.find((test) => test.id === id)?.lastUnfinishedUserRun?.id)
        .filter((id) => !!id);

      if (testRunsIds?.length === 0) {
        toasts.show({
          content: t('noRunningTestRuns', 'No test runs to stop'),
          autoClose: 2000,
        });
        return;
      }

      await stopTestRuns(testRunsIds);

      dispatch(
        // @ts-expect-error Invalid type due to old RTK/Redux version
        testsApi.util.updateQueryData('getTests', { projectId }, (draft) => {
          const testsIds = ids || draft.map((test) => test.id);

          return draft.map((test) => {
            if (testsIds.includes(test.id)) {
              return {
                ...test,
                lastResult: test.lastResult
                  ? {
                      ...test.lastResult,
                      status: 'stopped',
                    }
                  : undefined,
                // FIXME: Without this test run status on tests list will not be updated
                lastUnfinishedUserRun: undefined,
              };
            }

            return test;
          });
        }),
      );
    },
    [dispatch, projectId, stopTestRuns, t, testsList],
  );

  return useMemo(() => [handleStop] as const, [handleStop]);
};
